// client/src/Master.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Style.css";

export default function Master() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Login form
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Data
  const [messages, setMessages] = useState([]);
  const [cardrooms, setCardrooms] = useState([]);
  const [loading, setLoading] = useState(false);

  // Invite form
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // Response inputs (keyed by message id)
  const [responses, setResponses] = useState({});
  const [respondLoading, setRespondLoading] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("master_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsLoggedIn(true);
      fetchData(token);
    }
  }, []);

  const fetchData = async (token) => {
    setLoading(true);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    try {
      const [msgsRes, roomsRes] = await Promise.all([
        axios.get("/api/master/messages"),
        axios.get("/api/master/cardrooms"),
      ]);
      setMessages(msgsRes.data);
      setCardrooms(roomsRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("master_token");
        setIsLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const { data } = await axios.post("/api/auth/master-login", {
        identifier,
        password,
      });
      localStorage.setItem("master_token", data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      setIsLoggedIn(true);
      fetchData(data.token);
    } catch (err) {
      setLoginError(err.response?.data?.error || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("master_token");
    delete axios.defaults.headers.common["Authorization"];
    setIsLoggedIn(false);
    setMessages([]);
    setCardrooms([]);
  };

  const handleInviteCardroom = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviteSuccess("");
    setInviteLoading(true);
    try {
      await axios.post("/api/master/invite-cardroom", {
        name: inviteName,
        email: inviteEmail,
      });
      setInviteSuccess(`Invitation sent to ${inviteName} at ${inviteEmail}`);
      setInviteName("");
      setInviteEmail("");
    } catch (err) {
      setInviteError(err.response?.data?.error || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRespond = async (msgId) => {
    const text = responses[msgId];
    if (!text?.trim()) return;

    setRespondLoading((prev) => ({ ...prev, [msgId]: true }));
    try {
      await axios.post(`/api/master/messages/${msgId}/respond`, {
        response: text,
      });
      // Remove from list after responding
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setResponses((prev) => {
        const copy = { ...prev };
        delete copy[msgId];
        return copy;
      });
    } catch (err) {
      console.error("Failed to respond:", err);
    } finally {
      setRespondLoading((prev) => ({ ...prev, [msgId]: false }));
    }
  };

  const inputStyle = {
    width: "100%",
    background: "var(--game-bg-tertiary)",
    border: "1px solid var(--game-border)",
    borderRadius: "var(--game-radius-sm)",
    padding: "0.75rem",
    color: "var(--game-text-primary)",
    fontSize: "16px",
    outline: "none",
    transition: "var(--game-transition)",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "var(--game-text-primary)",
    marginBottom: "0.5rem",
  };

  const thStyle = {
    padding: "0.75rem",
    textAlign: "left",
    color: "var(--game-text-secondary)",
    fontSize: "0.875rem",
  };

  const tdStyle = {
    padding: "0.75rem",
    color: "var(--game-text-primary)",
  };
  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="game" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
        <div
          style={{
            maxWidth: 400,
            width: "100%",
            background: "var(--game-bg-secondary)",
            border: "1px solid var(--game-border)",
            borderRadius: "var(--game-radius-lg)",
            padding: "2rem",
            boxShadow: "var(--game-shadow-lg)",
          }}
        >
          <h1
            style={{
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "var(--game-text-primary)",
              marginBottom: "1.5rem",
            }}
          >
            Master Login
          </h1>
          <form
            onSubmit={handleLogin}
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <input
              type="text"
              placeholder="Username"
              className="game-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              className="game-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {loginError && (
              <p style={{ color: "var(--game-danger)", textAlign: "center", fontSize: "0.875rem", margin: 0 }}>
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              className="game-btn game-btn--primary"
              style={{ width: "100%" }}
            >
              {loginLoading ? "Logging in..." : "Log In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="game">
      {/* Header */}
      <header className="game-header">
        <div className="game-header__container">
          <div className="game-header__left">
            <h1 className="game-title">Master Dashboard</h1>
          </div>
          <div className="game-header__right">
            <button
              onClick={handleLogout}
              className="game-nav__btn"
              style={{ border: "1px solid var(--game-border)" }}
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      <div className="game-container">
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: 700, color: "var(--game-text-primary)" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  border: "4px solid rgba(255,255,255,0.1)",
                  borderTop: "4px solid var(--game-primary)",
                  borderRadius: "50%",
                  width: 50,
                  height: 50,
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            {/* Invite New Cardroom */}
            <section className="game-section">
              <h2 className="game-section__title">Invite New Club</h2>
              <form
                onSubmit={handleInviteCardroom}
                className="game-form"
              >
                <input
                  type="text"
                  placeholder="Organization name"
                  className="game-input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  required
                  style={{ flex: 1, minWidth: 250 }}
                />
                <input
                  type="email"
                  placeholder="Email address"
                  className="game-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  style={{ flex: 1, minWidth: 250 }}
                />
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="game-btn game-btn--primary"
                >
                  {inviteLoading ? "Sending..." : "Send Invite"}
                </button>
              </form>
              {inviteError && (
                <p style={{ color: "var(--game-danger)", marginTop: "1rem", fontSize: "0.875rem" }}>
                  {inviteError}
                </p>
              )}
              {inviteSuccess && (
                <p style={{ color: "var(--game-success)", marginTop: "1rem", fontSize: "0.875rem" }}>
                  {inviteSuccess}
                </p>
              )}
            </section>

            {/* Support Tickets */}
            <section className="game-section">
              <h2 className="game-section__title">Support Tickets ({messages.length})</h2>
              {messages.length === 0 ? (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.9rem", fontStyle: "italic" }}>
                  No unanswered tickets.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        background: "rgba(255, 255, 255, 0.03)",
                        border: "1px solid var(--game-border-light)",
                        borderRadius: "var(--game-radius)",
                        padding: "1.25rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.75rem",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        <span
                          style={{
                            padding: "0.25rem 0.6rem",
                            background: "rgba(99, 102, 241, 0.15)",
                            color: "#818cf8",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {msg.cardroom_name}
                        </span>
                        <span style={{ color: "var(--game-text-secondary)", fontSize: "0.8rem" }}>
                          {msg.submitted_by} •{" "}
                          {new Date(msg.created_at).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <p
                        style={{
                          color: "var(--game-text-primary)",
                          fontSize: "0.9375rem",
                          margin: "0 0 1rem 0",
                          lineHeight: 1.6,
                        }}
                      >
                        {msg.message}
                      </p>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
                        <textarea
                          placeholder="Type your response..."
                          className="game-input"
                          value={responses[msg.id] || ""}
                          onChange={(e) =>
                            setResponses((prev) => ({
                              ...prev,
                              [msg.id]: e.target.value,
                            }))
                          }
                          rows={2}
                          style={{
                            flex: 1,
                            resize: "vertical",
                            minHeight: "60px",
                          }}
                        />
                        <button
                          onClick={() => handleRespond(msg.id)}
                          disabled={respondLoading[msg.id] || !responses[msg.id]?.trim()}
                          className="game-btn game-btn--primary"
                          style={{ height: "42px" }}
                        >
                          {respondLoading[msg.id] ? "Sending..." : "Respond"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* All Cardrooms */}
            <section className="game-section">
              <h2 className="game-section__title">All Cardrooms ({cardrooms.length})</h2>
              {cardrooms.length === 0 ? (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.9rem", fontStyle: "italic" }}>No cardrooms yet.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", borderBottom: "1px solid var(--game-border)" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>Users</th>
                        <th style={thStyle}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cardrooms.map((cr) => (
                        <tr key={cr.id}>
                          <td style={tdStyle}>{cr.name}</td>
                          <td style={tdStyle}>{cr.user_count}</td>
                          <td style={{ ...tdStyle, color: "var(--game-text-secondary)", fontSize: "0.875rem" }}>
                            {cr.created_at
                              ? new Date(cr.created_at).toLocaleString([], {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
